import React from 'react';
import { Dialog } from '@ygtechnologies/rubus-ui';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { useAsyncState } from '@/utils/hooks';
import { getPullRequest, PullRequestDetail, SourceResult } from '@/services/systemHealth';
import shared from './SystemHealthPage.module.scss';
import theme from './PrDetailsModal.module.scss';

const checkOrder = { failing: 0, pending: 1, passing: 2, skipped: 3 };
const checkLabel = { failing: 'Failing', pending: 'Running', passing: 'Passed', skipped: 'Skipped' };
const reviewLabel = {
    APPROVED: 'Approved',
    CHANGES_REQUESTED: 'Changes requested',
    COMMENTED: 'Commented',
    DISMISSED: 'Dismissed',
};

const fmt = (n: number) => n.toLocaleString('en-GB');

const Ext: React.FC<React.PropsWithChildren<{ href: string }>> = ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer">
        {children}
    </a>
);

/** Unified diff hunk, one line per row, coloured by its +/- prefix. */
const Patch: React.FC<{ patch: string }> = ({ patch }) => (
    <pre className={theme.patch}>
        {patch.split('\n').map((line, i) => (
            <div
                key={i}
                className={
                    line.startsWith('+')
                        ? theme.lineAdded
                        : line.startsWith('-')
                          ? theme.lineDeleted
                          : line.startsWith('@@')
                            ? theme.lineHunk
                            : undefined
                }>
                {line || ' '}
            </div>
        ))}
    </pre>
);

const Details: React.FC<{ pr: PullRequestDetail }> = ({ pr }) => {
    const checks = [...pr.checks].sort((a, b) => checkOrder[a.state] - checkOrder[b.state]);
    const failing = checks.filter((c) => c.state === 'failing').length;
    return (
        <div className={theme.body}>
            <p className={shared.muted}>
                {pr.repo}#{pr.number} · {pr.author} · <code>{pr.head}</code> → <code>{pr.base}</code>
                {pr.mergeable === 'CONFLICTING' && <span className={theme.conflict}> · Has merge conflicts</span>}
                {' · '}
                <Ext href={pr.url}>Open on GitHub</Ext>
                {pr.linearId && (
                    <>
                        {' · '}
                        <Ext href={`https://linear.app/rucoria/issue/${pr.linearId}`}>{pr.linearId}</Ext>
                    </>
                )}
            </p>

            <h4 className={theme.section}>
                Checks{' '}
                {checks.length > 0 && (
                    <span className={failing ? theme.failing : shared.muted}>
                        {failing ? `${failing} failing` : 'all passing or running'}
                    </span>
                )}
            </h4>
            {checks.length === 0 ? (
                <p className={shared.muted}>No checks on the latest commit.</p>
            ) : (
                <ul className={theme.list}>
                    {checks.map((c) => (
                        <li key={c.name}>
                            <span className={`${theme.dot} ${theme[c.state]}`} aria-hidden />
                            {c.url ? <Ext href={c.url}>{c.name}</Ext> : c.name}
                            <span className={shared.muted}> · {checkLabel[c.state]}</span>
                        </li>
                    ))}
                </ul>
            )}

            <h4 className={theme.section}>Reviews</h4>
            {pr.reviews.length === 0 && pr.requestedReviewers.length === 0 ? (
                <p className={shared.muted}>No reviewers yet.</p>
            ) : (
                <ul className={theme.list}>
                    {pr.reviews.map((r) => (
                        <li key={r.author}>
                            {r.author} <span className={shared.muted}>· {reviewLabel[r.state] ?? r.state}</span>
                        </li>
                    ))}
                    {pr.requestedReviewers
                        .filter((name) => !pr.reviews.some((r) => r.author === name))
                        .map((name) => (
                            <li key={name}>
                                {name} <span className={shared.muted}>· Waiting</span>
                            </li>
                        ))}
                </ul>
            )}

            {pr.body?.trim() && (
                <>
                    <h4 className={theme.section}>Description</h4>
                    <p className={theme.description}>{pr.body}</p>
                </>
            )}

            <h4 className={theme.section}>
                Files changed <span className={shared.muted}>{fmt(pr.changedFiles)}</span>{' '}
                <span className={theme.added}>+{fmt(pr.additions)}</span>{' '}
                <span className={theme.deleted}>−{fmt(pr.deletions)}</span>
            </h4>
            {pr.files.map((f) => (
                <details key={f.path} className={theme.file}>
                    <summary>
                        <span className={theme.path}>{f.path}</span>
                        {f.status !== 'modified' && <span className={shared.muted}> {f.status}</span>}
                        <span className={theme.fileStats}>
                            <span className={theme.added}>+{fmt(f.additions)}</span>{' '}
                            <span className={theme.deleted}>−{fmt(f.deletions)}</span>
                        </span>
                    </summary>
                    {f.patch ? (
                        <Patch patch={f.patch} />
                    ) : (
                        <p className={shared.muted}>
                            No diff to show (binary or too large). <Ext href={`${pr.url}/files`}>View on GitHub</Ext>
                        </p>
                    )}
                </details>
            ))}
            {pr.changedFiles > pr.files.length && (
                <p className={shared.muted}>
                    Showing {pr.files.length} of {fmt(pr.changedFiles)} files.{' '}
                    <Ext href={`${pr.url}/files`}>All files on GitHub</Ext>
                </p>
            )}
        </div>
    );
};

const PrDetailsModal: React.FC<{ repo: string; number: number; title: string; onClose(): void }> = ({
    repo,
    number,
    title,
    onClose,
}) => {
    const [result] = useAsyncState<SourceResult<PullRequestDetail> | null>(
        () => getPullRequest(repo, number),
        [repo, number],
        null
    );

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>{title}</Dialog.Header>
                {!result ? (
                    <LoadingIndicator />
                ) : result.data ? (
                    <Details pr={result.data} />
                ) : (
                    <p className={theme.failing}>{result.error ?? 'Could not load this pull request.'}</p>
                )}
            </Dialog.Content>
        </Dialog>
    );
};

export default PrDetailsModal;
